import { useState } from 'react';
import { useLingui, Trans } from '@lingui/react/macro';
import { useGlobalStore } from '@/store/useGlobalStore';
import { FactionSide, GameScene, MapConfig, UnitEntry } from '@/core/types/global.types';
import { CUMANS_UNITS_DATA, CumanUnitId, RUTHENIAN_UNITS_DATA, RuthenianUnitId, UNITS_DATA, type UnitId } from '@/data/units';
import { i18n } from '@lingui/core';
import logo from '@assets/rw_logoh.png';

// ─── Sub-components ──────────────────────────────────────────────────────────

function Slider({
    label, min, max, value, unit = '%',
    onChange,
}: {
    label: string; min: number; max: number; value: number; unit?: string;
    onChange: (v: number) => void;
}) {
    return (
        <div className="flex flex-col gap-1">
            <div className="flex justify-between text-xs uppercase tracking-widest text-amber-500/60">
                <span>{label}</span>
                <span className="text-amber-300/80">{value}{unit}</span>
            </div>
            <input
                type="range" min={min} max={max} value={value}
                onChange={e => onChange(Number(e.target.value))}
                className="w-full h-0.5 appearance-none bg-amber-900/40 accent-amber-500 cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-amber-900/60">
                <span>{min}{unit}</span><span>{max}{unit}</span>
            </div>
        </div>
    );
}

function UnitSelector({
    title,
    available,
    selected,
    onChange,
}: {
    title: string;
    available: UnitId[];
    selected: UnitEntry[];
    onChange: (units: UnitEntry[]) => void;
}) {
    const remove = (index: number) => {
        const entry = selected[index];
        if (!entry) return;
        if (entry.count <= 1) {
            onChange(selected.filter((_, i) => i !== index));
        } else {
            onChange(selected.map((u, i) => i === index ? { ...u, count: u.count - 1 } : u));
        }
    };

    const add = (unitId: UnitId) => {
        if (selected.length >= 8) return;
        onChange([...selected, { id: `${unitId}_${crypto.randomUUID()}`, unitId, count: 1 }]);
    };

    const setCount = (index: number, count: number) => {
        onChange(selected.map((u, i) => i === index ? { ...u, count: Math.max(1, Math.min(99, count)) } : u));
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="text-xs uppercase tracking-widest text-amber-500/60 mb-1">{title}</div>

            <div className="flex flex-wrap gap-2">
                {available.map(id => (
                    <button
                        key={id}
                        onClick={() => add(id)}
                        className="px-3 py-1 text-xs border border-amber-900/40 text-amber-300/60
                                   hover:border-amber-500/60 hover:text-amber-200 transition-all
                                   uppercase tracking-wider cursor-pointer"
                    >
                        + {i18n._(UNITS_DATA[id].unitName)}
                    </button>
                ))}
            </div>

            {selected.length > 0 && (
                <div className="flex flex-col gap-1 mt-1">
                    {selected.map((entry, index) => (
                        <div key={entry.id}
                            className="flex items-center gap-2 px-3 py-1.5
                                       border border-amber-700/30 bg-amber-900/10">
                            <span className="flex-1 text-xs text-amber-200/80 uppercase tracking-wider">
                                {i18n._(UNITS_DATA[entry.unitId].unitName)}
                            </span>
                            <div className="flex items-center gap-1">
                                <button onClick={() => remove(index)}
                                    className="w-5 h-5 flex items-center justify-center border border-amber-900/40
                                               text-amber-500/60 hover:border-amber-500 hover:text-amber-300
                                               transition-all cursor-pointer text-xs">
                                    −
                                </button>
                                <input
                                    type="number" value={entry.count}
                                    onChange={e => setCount(index, Number(e.target.value))}
                                    className="w-12 text-center text-xs bg-transparent border border-amber-900/40
                                               text-amber-200/80 py-0.5 focus:outline-none focus:border-amber-500/60"
                                    min={1} max={99}
                                />
                                <button onClick={() => setCount(index, entry.count + 1)}
                                    className="w-5 h-5 flex items-center justify-center border border-amber-900/40
                                               text-amber-500/60 hover:border-amber-500 hover:text-amber-300
                                               transition-all cursor-pointer text-xs">
                                    +
                                </button>
                            </div>
                            <button
                                onClick={() => onChange(selected.filter((_, i) => i !== index))}
                                disabled={selected.length <= 1}
                                className="text-amber-900/60 hover:text-amber-500 transition-colors text-xs cursor-pointer ml-1 disabled:opacity-20 disabled:cursor-not-allowed"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Main component ──────────────────────────────────────────────────────────

export const WorldUI = () => {
    const { t } = useLingui();
    const setScene = useGlobalStore((state) => state.setScene);
    const setPlayerSide = useGlobalStore((state) => state.setPlayerSide);
    const setMapConfig = useGlobalStore((state) => state.setMapConfig);
    
    const rutheniaUnitIds = Object.keys(RUTHENIAN_UNITS_DATA) as RuthenianUnitId[];
    const cumanUnitIds = Object.keys(CUMANS_UNITS_DATA) as CumanUnitId[];

    const [open, setOpen] = useState(false);
    const cfg = useGlobalStore((state) => state.mapConfig);
    const set = <K extends keyof MapConfig>(key: K, val: MapConfig[K]) =>
        setMapConfig({ ...cfg, [key]: val });

    const startBattle = (side: FactionSide) => {
        setPlayerSide(side);
        setMapConfig(cfg);
        setScene(GameScene.BATTLE);
    };

    return (
        <div className="fixed top-0 left-0 w-full h-[100dvh] grid grid-cols-1 lg:grid-cols-[1fr_minmax(320px,600px)_1fr] pointer-events-auto">

            {/* Ліва колонка */}
            <div
                className="hidden lg:block bg-no-repeat overflow-hidden bg-gryd"
            />

            {/* Центральна колонка */}
            <div className="flex flex-col h-full overflow-y-auto pb-[env(safe-area-inset-bottom)]">

                {/* Лого */}
                <div className="flex justify-center pt-8 lg:pt-12 shrink-0 px-4">
                    <img
                        src={logo}
                        className="w-full max-w-2xl h-auto max-h-[20vh] pointer-events-none select-none drop-shadow-2xl object-contain"
                    />
                </div>

                {/* Текст + кнопки */}
                <div className="flex flex-col items-center px-4 py-8 gap-6">
                    <div className="text-center space-y-4 w-full max-w-2xl pointer-events-auto">
                        <p className="text-muted text-lg sm:text-xl leading-relaxed font-serif font-kyiv">
                            <Trans>...that same summer the Cumans came to the Kyiv frontiers.<br/>
                            And the Ruthenian Druzhyna sallied forth to confront them.<br/></Trans>
                        </p>
                        <p className="text-main uppercase tracking-[0.2em] font-bold font-kyiv">
                            <Trans>Who shall survive this day?</Trans>
                        </p>
                    </div>

                    {/* Кнопки фракцій */}
                    <div className="flex flex-wrap justify-center gap-6 pointer-events-auto w-full">
                        <button
                            onClick={() => startBattle(FactionSide.RUTHENIA)}
                            className="bb-btn bb-btn-lg flex-1 min-w-[200px] max-w-[300px] whitespace-nowrap">
                            <Trans>Ruthenian Druzhyna</Trans>
                        </button>
                        <button
                            onClick={() => startBattle(FactionSide.CUMANS)}
                            className="bb-btn bb-btn-lg flex-1 min-w-[200px] max-w-[300px] whitespace-nowrap">
                            <Trans>Cuman Horde</Trans>
                        </button>
                    </div>

                    {/* Advanced panel */}
                    <div className="w-full max-w-2xl pointer-events-auto">
                        <button
                            onClick={() => setOpen(v => !v)}
                            className="w-full flex items-center justify-between px-4 py-2
                                       border border-amber-900/30 text-amber-700/60
                                       hover:border-amber-700/50 hover:text-amber-500/80
                                       transition-all cursor-pointer uppercase tracking-widest text-xs"
                        >
                            <span><Trans>Advanced Settings</Trans></span>
                            <span className="transition-transform duration-300"
                                style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
                        </button>

                        {open && (
                            <div className="border border-t-0 border-amber-900/30 bg-slate-950/80 px-5 py-5 flex flex-col gap-6">

                                {/* Map */}
                                <div className="flex flex-col gap-4">
                                    <div className="text-[11px] uppercase tracking-[0.3em] text-amber-700/70 border-b border-amber-900/20 pb-1">
                                        <Trans>Map</Trans>
                                    </div>
                                    <Slider label={t`Size`} min={16} max={32} value={cfg.size} unit=""
                                        onChange={v => set('size', v)} />
                                    <Slider label={t`Hills`} min={0} max={25} value={cfg.hillChance}
                                        onChange={v => set('hillChance', v)} />
                                    <Slider label={t`Obstacles`} min={0} max={25} value={cfg.obstacleChance}
                                        onChange={v => set('obstacleChance', v)} />
                                    <div className="flex flex-col gap-1">
                                        <div className="text-xs uppercase tracking-widest text-amber-500/60">Seed</div>
                                        <input
                                            type="text"
                                            value={cfg.seed}
                                            onChange={e => set('seed', e.target.value)}
                                            placeholder={t`random`}
                                            className="bg-transparent border border-amber-900/40 px-3 py-1.5
                                                       text-xs text-amber-200/80 placeholder-amber-900/40
                                                       focus:outline-none focus:border-amber-500/60 w-full"
                                        />
                                    </div>
                                </div>

                                {/* Units */}
                                <div className="flex flex-col gap-4">
                                    <div className="text-[11px] uppercase tracking-[0.3em] text-amber-700/70 border-b border-amber-900/20 pb-1">
                                        <Trans>Warband Roster</Trans>
                                    </div>
                                    <UnitSelector
                                        title={t`Ruthenian Druzhyna`}
                                        available={rutheniaUnitIds}
                                        selected={cfg.playerUnits}
                                        onChange={v => set('playerUnits', v)}
                                    />
                                    <UnitSelector
                                        title={t`Cuman Horde`}
                                        available={cumanUnitIds}
                                        selected={cfg.enemyUnits}
                                        onChange={v => set('enemyUnits', v)}
                                    />
                                </div>

                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Права колонка */}
            <div
                className="hidden lg:block bg-no-repeat overflow-hidden bg-kipchak"
            />
        </div>
    );
};
